import type { GlanceDir } from '../sim/types';
import type { InputAction, InputSource, SourceSample } from './InputSource';

const DEADZONE = 0.09;

function dz(v: number): number {
  return Math.abs(v) < DEADZONE ? 0 : (v - Math.sign(v) * DEADZONE) / (1 - DEADZONE);
}

/**
 * Mando estándar (Gamepad API):
 *   RT acelerar · LT frenar · stick izquierdo dirección · stick derecho espejos
 *   X (mantener) embrague · LB/RB marcha abajo/arriba · A (mantener) arrancar
 *   B freno de mano · Y cámara · cruceta ←/→ intermitentes · ↑ luces · ↓ cinturón
 *   Pulsar stick derecho: claxon · Start pausa
 */
export class GamepadSource implements InputSource {
  private actions: InputAction[] = [];
  private prevButtons: boolean[] = [];

  private pad(): Gamepad | null {
    if (typeof navigator === 'undefined' || !navigator.getGamepads) return null;
    for (const p of navigator.getGamepads()) if (p && p.connected) return p;
    return null;
  }

  sample(_dt: number): SourceSample {
    const pad = this.pad();
    if (!pad) {
      return {
        throttle: 0,
        brake: 0,
        steer: 0,
        glance: 'none',
        clutchAnalog: 0,
        clutchHold: false,
        clutchFeed: false,
        clutchMomentary: false,
      };
    }

    const throttle = pad.buttons[7]?.value ?? 0;
    const brake = pad.buttons[6]?.value ?? 0;
    const steer = dz(pad.axes[0] ?? 0);
    const rx = dz(pad.axes[2] ?? 0);
    const ry = dz(pad.axes[3] ?? 0);
    let glance: GlanceDir = 'none';
    if (ry > 0.55) glance = 'rear';
    else if (rx < -0.55) glance = 'left';
    else if (rx > 0.55) glance = 'right';

    // Flancos de botones
    const pressed = (i: number): boolean => pad.buttons[i]?.pressed ?? false;
    const edge = (i: number): boolean => pressed(i) && !(this.prevButtons[i] ?? false);
    const released = (i: number): boolean => !pressed(i) && (this.prevButtons[i] ?? false);

    if (edge(4)) this.actions.push({ type: 'gearCycle', dir: -1 }); // LB baja marcha
    if (edge(5)) this.actions.push({ type: 'gearCycle', dir: 1 }); // RB sube marcha
    if (edge(14)) this.actions.push({ type: 'signal', side: 'left' });
    if (edge(15)) this.actions.push({ type: 'signal', side: 'right' });
    if (edge(1)) this.actions.push({ type: 'handbrake' });
    if (edge(12)) this.actions.push({ type: 'lights' });
    if (edge(13)) this.actions.push({ type: 'belt' });
    if (edge(3)) this.actions.push({ type: 'camera' });
    if (edge(9)) this.actions.push({ type: 'pause' });
    if (edge(0)) this.actions.push({ type: 'ignitionDown', down: true });
    if (released(0)) this.actions.push({ type: 'ignitionDown', down: false });
    if (edge(11)) this.actions.push({ type: 'hornDown', down: true });
    if (released(11)) this.actions.push({ type: 'hornDown', down: false });
    this.prevButtons = pad.buttons.map((b) => b.pressed);

    return {
      throttle,
      brake,
      steer,
      glance,
      clutchAnalog: 0,
      clutchHold: false,
      clutchFeed: false,
      clutchMomentary: pressed(2), // X mantiene el embrague pisado
    };
  }

  drainActions(): InputAction[] {
    const out = this.actions;
    this.actions = [];
    return out;
  }

  dispose(): void {
    // La Gamepad API no necesita limpieza
  }
}
